import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase.js";
import { todayStr } from "../lib/format.js";
import { removeFile } from "../lib/storage.js";

/**
 * Equipment data access — the pattern the remaining tabs copy.
 *
 * Three things differ from v10, where all data sat in one in-memory object:
 *
 * 1. Reads are asynchronous and can fail, so every consumer handles loading and
 *    error states.
 * 2. Writes go to Postgres, so the query cache is invalidated afterwards rather
 *    than the local object being mutated.
 * 3. Column names are snake_case and typed. v10 stored "not set" as "" for
 *    every field; Postgres rejects that for date and numeric columns, so the
 *    mappers below normalise it — the same trap that broke the first migration
 *    run.
 */

const SELECT =
  "id,slug,name,category,make,model,serial,year,hour_meter,day_rate,week_rate," +
  "month_rate,status,notes,photo_path,manual_path,manual_name,next_service_date,next_service_hours";

/** Database row -> form state. */
export function rowToForm(row = {}) {
  return {
    name: row.name ?? "",
    category: row.category ?? "",
    make: row.make ?? "",
    model: row.model ?? "",
    serial: row.serial ?? "",
    year: row.year ?? "",
    hourMeter: row.hour_meter ?? "",
    dayRate: row.day_rate ?? "",
    weekRate: row.week_rate ?? "",
    monthRate: row.month_rate ?? "",
    status: row.status ?? "available",
    notes: row.notes ?? "",
    nextServiceDate: row.next_service_date ?? "",
    nextServiceHours: row.next_service_hours ?? "",
    photoPath: row.photo_path ?? null,
    manualPath: row.manual_path ?? null,
    manualName: row.manual_name ?? null,
  };
}

const blankToNull = (v) => (v === "" || v === undefined ? null : v);

/**
 * Same derivation migrate-backup.mjs used, so rows created in the app match
 * migrated ones. The public availability view joins on equipment, and Phase 3's
 * booking widget keys off `slug` — a machine added here with a null slug would
 * be invisible to the website.
 */
export const slugify = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || null;

/** Form state -> database row. */
export function formToRow(f) {
  return {
    name: f.name.trim(),
    category: blankToNull(f.category),
    make: blankToNull(f.make),
    model: blankToNull(f.model),
    serial: blankToNull(f.serial),
    year: blankToNull(f.year),
    hour_meter: blankToNull(f.hourMeter),
    day_rate: Number(f.dayRate) || 0,
    week_rate: Number(f.weekRate) || 0,
    month_rate: Number(f.monthRate) || 0,
    status: f.status,
    notes: blankToNull(f.notes),
    // date column: "" is rejected by Postgres, null is not
    next_service_date: blankToNull(f.nextServiceDate),
    next_service_hours: blankToNull(f.nextServiceHours),
    photo_path: f.photoPath ?? null,
    manual_path: f.manualPath ?? null,
    manual_name: f.manualName ?? null,
  };
}

export function useEquipment() {
  return useQuery({
    queryKey: ["equipment"],
    queryFn: async () => {
      const { data, error } = await supabase.from("equipment").select(SELECT).order("name");
      if (error) throw new Error(error.message);
      return data;
    },
  });
}

/** Only what equipmentLiveStatus needs — not the whole rentals table. */
export function useRentalsForStatus() {
  return useQuery({
    queryKey: ["rentals", "for-status"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("rentals")
        .select("id,equipment_id,status,start_date,end_date");
      if (error) throw new Error(error.message);
      return data;
    },
  });
}

export function useSaveEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, form, previous }) => {
      const row = formToRow(form);
      // Only set on create. Changing an existing slug would break any website
      // link already pointing at that machine.
      if (!id) row.slug = slugify(row.name);

      const q = id
        ? supabase.from("equipment").update(row).eq("id", id).select("id")
        : supabase.from("equipment").insert([row]).select("id");
      const { data, error } = await q;
      if (error) {
        // 23505: slug is unique, and two machines can reasonably share a name.
        if (error.code === "23505") {
          throw new Error(
            "Another machine already uses that name. Make it more specific — " +
              "add the serial or year, for example."
          );
        }
        throw new Error(error.message);
      }
      // An update matching zero rows returns [] with no error — that is RLS
      // filtering, not success. Surfacing it beats a silent no-op.
      if (!data?.length) {
        throw new Error(
          "The database accepted the request but changed nothing. This usually " +
            "means a row level security policy blocked it."
        );
      }
      // Delete superseded files only after the row is safely saved. Doing it at
      // the moment the user clicks "Remove" would destroy the file even if they
      // then cancelled, leaving the record pointing at nothing.
      for (const key of ["photo_path", "manual_path"]) {
        const before = previous?.[key];
        if (before && before !== row[key]) await removeFile(before);
      }

      return data[0];
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["equipment"] }),
  });
}

export function useDeleteEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, paths = [] }) => {
      const { data, error } = await supabase.from("equipment").delete().eq("id", id).select("id");
      if (error) {
        // Postgres 23503: the machine is referenced by a rental or maintenance row.
        if (error.code === "23503") {
          throw new Error(
            "This machine has rentals or maintenance records attached, so it cannot " +
              "be deleted. Set its status to Retired instead — that keeps the history intact."
          );
        }
        throw new Error(error.message);
      }
      if (!data?.length) throw new Error("Nothing was deleted — a security policy may have blocked it.");
      // The row is gone, so its files are unreachable. Remove them rather than
      // leaving driver's licences and manuals orphaned in the bucket.
      for (const p of paths.filter(Boolean)) await removeFile(p);
      return id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["equipment"] }),
  });
}

/** Ported verbatim from v10, reading snake_case columns. */
export function equipmentLiveStatus(eq, rentals = []) {
  if (eq.status === "maintenance" || eq.status === "retired") return eq.status;
  const t = todayStr();
  const activeNow = rentals.some((r) => r.equipment_id === eq.id && r.status === "out");
  if (activeNow) return "out";
  const reservedToday = rentals.some(
    (r) => r.equipment_id === eq.id && r.status === "reserved" && r.start_date <= t && r.end_date >= t
  );
  return reservedToday ? "reserved" : "available";
}
